/**
 * Customer Onboarding API helpers (Phase 5 + Phase O5 2026-05-22)
 *
 * FOUR calls, in order:
 *   1. extractPdfMarkdown — POST PDF (base64) to Knowledge-MCP /extract via
 *      gateway, returns Docling-rendered markdown.
 *   2. prepareOnboarding — POST {orgName, pdfFilename, pdfSizeBytes} to
 *      /api/onboard/prepare. The orchestrator resolves the org, bootstraps
 *      the default password + document folder sets idempotently, and
 *      returns {pipelineId, orgId, folderIds, manualFollowUps}.
 *   3. runExtractJob — start-then-poll against the gateway-proxied
 *      /api/onboard/extract/jobs endpoints (Phase O7M / async extract-jobs).
 *      The orchestrator runs 6 focused agent passes server-side and returns
 *      the stitched proposal: {summary, quickNotes, prerequisites[],
 *      dependents[], blockerWarnings[], proposalNotes[]}.
 *   4. commitOnboarding — POST {pipelineId, proposal, excluded} to
 *      /api/onboard/commit. `proposal` is always the FULL proposal
 *      (Task 4c, 2026-08-05: exclusions moved server-side — see
 *      `excluded` below); the orchestrator handles wave-6a prereqs +
 *      wave-6b dependents with Tag-ref resolution, Wireless auto-vault
 *      Password follow-up (file + rename), and Quick Notes write. Returns
 *      the full per-step result.
 *
 * LEGACY (kept for backward-compat until v2 cleanup):
 *   buildBulkCreateItems + confirmAndWrite still work for proposals in the
 *   OLD flat shape ({locations[], configurations[], contacts[], passwords[]}).
 *   New flow uses prepareOnboarding + commitOnboarding instead.
 */

import { callMCPTool } from './gateway.js';

const GATEWAY_URL = import.meta.env.VITE_CSG_GATEWAY_URL || '/api';

// ─── 1. Extract ───────────────────────────────────────────────────────────

/**
 * POST a base64-encoded PDF to the Knowledge-MCP /extract endpoint (via gateway).
 * Returns Docling-extracted markdown synchronously. Typical AUSSL-class doc:
 * 3–10 seconds.
 *
 * @param {string} fileBase64 - base64-encoded PDF bytes (no data: prefix)
 * @param {string} filename
 * @returns {Promise<{markdown: string, pages: number, metadata: object}>}
 */
export async function extractPdfMarkdown(fileBase64, filename) {
  const gatewayUrl = import.meta.env.VITE_CSG_GATEWAY_URL || '/api';
  const token = localStorage.getItem('accessToken');
  const response = await fetch(`${gatewayUrl}/api/knowledge/extract`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ file_base64: fileBase64, filename }),
  });
  if (!response.ok) {
    const text = await response.text();
    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = { error: 'unknown', message: text };
    }
    if (parsed.error === 'no_text_layer') {
      throw new Error(
        'No text layer found in PDF. It may be a scanned image — OCR is disabled in v1.'
      );
    }
    throw new Error(parsed.message || `Extract failed (HTTP ${response.status})`);
  }
  return response.json();
}

// ─── 2. Prepare (orchestrator prep — orgs + folders) ─────────────────────

/**
 * POST to the gateway-proxied /api/onboard/prepare endpoint. The orchestrator
 * resolves the IT Glue org, ensures the 5 default password folders + 5
 * default document folders exist (idempotent — reuses prior runs' IDs for
 * the same org), and returns the folder ID map.
 *
 * Pass the returned `folderIds` into `runExtractJob` so the agent can set
 * `passwordFolderId` on every Password it proposes.
 *
 * @param {Object} req
 * @param {string} req.orgName - customer's IT Glue org name (fuzzy-resolved)
 * @param {string} req.userId  - operator id (used in X-User-ID header)
 * @param {string} [req.pdfFilename]
 * @param {number} [req.pdfSizeBytes]
 * @param {string} [req.sourceMarkdown] - raw Docling markdown (Phase O7b);
 *   persisted alongside the run so prompt iteration can re-feed the same
 *   input later and so operators can tell agent misses apart from
 *   extraction misses. Pass `extractPdfMarkdown().markdown` here.
 * @returns {Promise<{pipeline_id: string, org_id: string, org_name: string, folder_ids: object, steps: Array, manual_follow_ups: Array}>}
 */
export async function prepareOnboarding({ orgName, userId, pdfFilename, pdfSizeBytes, sourceMarkdown }) {
  const token = localStorage.getItem('accessToken');
  const response = await fetch(`${GATEWAY_URL}/api/onboard/prepare`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-User-ID': userId || 'anonymous',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      org_name: orgName,
      pdf_filename: pdfFilename || null,
      pdf_size_bytes: pdfSizeBytes || null,
      source_markdown: sourceMarkdown || null,
    }),
  });
  if (!response.ok) {
    const text = await response.text();
    let parsed;
    try { parsed = JSON.parse(text); } catch { parsed = { detail: text }; }
    // 422 carries {detail: {step, detail}} from the orchestrator
    const stepInfo = parsed.detail || parsed;
    const message = typeof stepInfo === 'object'
      ? `Onboarding prepare failed at step '${stepInfo.step}': ${stepInfo.detail}`
      : String(stepInfo);
    throw new Error(message);
  }
  return response.json();
}

// ─── 3. Server-side multi-pass extract — async job + polling ──────────────
//
// The extraction takes 4-25 minutes (Foundry business-hours throttling) and
// ACA's default ingress kills any request at a hard 240 s, so the old
// blocking extractProposalFromMarkdown() call could not survive in prod.
// Replaced by start-then-poll: POST /extract/jobs returns in <1 s (inline
// result on a cache hit), then GET /extract/jobs/{id} every 5 s until the
// job completes. Use runExtractJob() — the start/get helpers are exported
// for tests and future callers.

const EXTRACT_POLL_INTERVAL_MS = 5000;
// Reject on the (N+1)th consecutive failed poll; any success resets the count.
const EXTRACT_POLL_MAX_CONSECUTIVE_FAILURES = 5;

/**
 * Start an extract job (or get the finished result inline on a cache hit).
 *
 * @returns {Promise<{status: string, result?: object, job_id?: string, reused?: boolean}>}
 *   {status:'completed', result} — cache hit, result is the full extract payload
 *   {job_id, status, reused: true} — an identical job is already in flight
 *   {job_id, status:'queued'} — fresh job started
 */
export async function startExtractJob({
  orgId, orgName, markdown, folderIds, userId, forceRefresh = false,
}) {
  const token = localStorage.getItem('accessToken');
  const response = await fetch(`${GATEWAY_URL}/api/onboard/extract/jobs`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-User-ID': userId || 'anonymous',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      org_id: orgId,
      org_name: orgName,
      markdown,
      folder_ids: folderIds || {},
      force_refresh: forceRefresh,
    }),
  });
  if (!response.ok) {
    const text = await response.text();
    let parsed;
    try { parsed = JSON.parse(text); } catch { parsed = { detail: text }; }
    const detail = parsed.detail || parsed;
    const message = typeof detail === 'string'
      ? detail
      : `Onboarding extract failed (HTTP ${response.status})`;
    throw new Error(message);
  }
  return response.json();
}

/**
 * Poll one extract job. Throws with err.status set on a non-2xx response so
 * the poll loop can special-case 404 (job TTL-cleaned or never existed).
 */
export async function getExtractJob({ jobId, userId }) {
  const token = localStorage.getItem('accessToken');
  const response = await fetch(`${GATEWAY_URL}/api/onboard/extract/jobs/${jobId}`, {
    headers: {
      'X-User-ID': userId || 'anonymous',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!response.ok) {
    const err = new Error(`Extract job poll failed (HTTP ${response.status})`);
    err.status = response.status;
    throw err;
  }
  return response.json();
}

/** DOMException-style AbortError shared by sleep() and the poll loop. */
function abortError() {
  const e = new Error('The operation was aborted.');
  e.name = 'AbortError';
  return e;
}

/** Abortable sleep — aborting rejects with a DOMException-style AbortError. */
function sleep(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortError());
      return;
    }
    const t = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    function onAbort() {
      clearTimeout(t);
      reject(abortError());
    }
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

const STALE_JOB_MESSAGE =
  'The extraction was interrupted — the server likely restarted. '
  + 'Re-upload the document to resume; finished work is cached, so the retry is quick.';

const AI_UNAVAILABLE_MESSAGE =
  'The AI service (Azure AI Foundry) failed on every extraction step, so nothing was extracted. '
  + 'Nothing was cached — try the upload again later.';

/**
 * Run an extraction start-to-finish: start the job (instant result on a
 * cache hit), then poll every 5 s until it completes. Resolves with the same
 * payload shape the old blocking extract call resolved with.
 *
 * Aborting the signal stops POLLING ONLY — the job keeps running server-side
 * and its result lands in the extraction cache, so re-uploading later
 * re-attaches (dedup) or hits the cache.
 *
 * @param {Object} req - startExtractJob's params plus:
 * @param {(progress: {passes: Object}) => void} [req.onProgress] - called on
 *   each successful poll with the per-pass progress map
 * @param {AbortSignal} [req.signal]
 */
export async function runExtractJob({
  orgId, orgName, markdown, folderIds, userId, forceRefresh = false, onProgress, signal,
}) {
  const start = await startExtractJob({
    orgId, orgName, markdown, folderIds, userId, forceRefresh,
  });
  if (start.status === 'completed') return start.result;

  const jobId = start.job_id;
  let consecutiveFailures = 0;
  for (;;) {
    await sleep(EXTRACT_POLL_INTERVAL_MS, signal);
    let job;
    try {
      job = await getExtractJob({ jobId, userId });
      consecutiveFailures = 0;
    } catch (err) {
      if (err.name === 'AbortError') throw err;
      if (err.status === 404) {
        throw new Error('Extraction job not found — please re-upload the document.');
      }
      consecutiveFailures += 1;
      if (consecutiveFailures > EXTRACT_POLL_MAX_CONSECUTIVE_FAILURES) {
        throw new Error(`Lost contact with the extraction job after ${consecutiveFailures} failed polls: ${err.message}`);
      }
      continue;
    }
    if (signal?.aborted) throw abortError();
    if (job.status === 'completed') return job.result;
    if (job.status === 'failed') {
      if (job.error === 'stale') throw new Error(STALE_JOB_MESSAGE);
      if (job.error === 'ai_unavailable') throw new Error(AI_UNAVAILABLE_MESSAGE);
      throw new Error('Extraction failed on the server — check agent-backend logs.');
    }
    if (onProgress) onProgress(job.progress || { passes: {} });
  }
}

// ─── 4. Commit (orchestrator write) ───────────────────────────────────────

/**
 * POST to the gateway-proxied /api/onboard/commit endpoint. The orchestrator
 * runs the 2-wave bulk write (prerequisites → dependents with Tag-ref
 * resolution), the Wireless auto-vault Password follow-up (file + rename),
 * and the Quick Notes write — all deterministic, no agent involvement.
 *
 * Task 4c (2026-08-05): `proposal` must always be the FULL, unfiltered
 * proposal — never pre-filter `prerequisites`/`dependents` by tick state
 * before calling this. `__prereq_index:N__` references (e.g. a synthetic
 * vendor-support contact the extractor appends at the end of
 * `prerequisites`) are computed against the ORIGINAL array position; a
 * client-side filter renumbers the array and resolves those references to
 * the wrong record. Pass the UNTICKED indices via `excluded` instead — the
 * server drops them before the same resolution runs, using the machinery
 * that already keeps original positions correct for its own drops (see
 * `orchestrator.commit()`'s `_apply_exclusions`).
 *
 * @param {Object} req
 * @param {string} req.pipelineId - from prepareOnboarding's response
 * @param {Object} req.proposal   - the FULL (possibly user-edited) agent
 *   proposal — do not filter `prerequisites`/`dependents` before passing it
 * @param {{prerequisites?: number[], dependents?: number[]}} [req.excluded] -
 *   indices into `proposal.prerequisites`/`proposal.dependents` (positions
 *   in those arrays exactly as passed above) the operator unticked in the
 *   review wizard. Optional — omitted or empty behaves exactly as a commit
 *   with nothing excluded.
 * @param {string} req.userId
 * @returns {Promise<{success: boolean, steps: Array, successful_ids: Array, blocker_warnings: Array, manual_follow_ups: Array, rollback_hint?: string, failure_step?: string, failure_detail?: string}>}
 */
export async function commitOnboarding({ pipelineId, proposal, excluded, userId }) {
  const token = localStorage.getItem('accessToken');
  const response = await fetch(`${GATEWAY_URL}/api/onboard/commit`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-User-ID': userId || 'anonymous',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      pipeline_id: pipelineId,
      proposal,
      excluded: excluded || { prerequisites: [], dependents: [] },
    }),
  });
  if (!response.ok) {
    const text = await response.text();
    let parsed;
    try { parsed = JSON.parse(text); } catch { parsed = { detail: text }; }
    const stepInfo = parsed.detail || parsed;
    const message = typeof stepInfo === 'object'
      ? `Onboarding commit failed at step '${stepInfo.step}': ${stepInfo.detail}`
      : String(stepInfo);
    throw new Error(message);
  }
  return response.json();
}

// ─── LEGACY: 3. Confirm + write (Phase 5 — kept for backward-compat) ──────
//
// The legacy path called IT-Glue-MCP `bulk_create` directly with the OLD
// flat proposal shape ({locations[], configurations[], ...}). The new
// orchestrator-backed path (commitOnboarding above) uses the NEW shape
// ({prerequisites[], dependents[], quickNotes, blockerWarnings[]}). These
// helpers are kept so anything that still depends on the old shape works,
// but the CustomerOnboardingPage now uses prepareOnboarding + commitOnboarding.

/**
 * Map the proposal's per-entity arrays into the bulk_create `items` shape
 * (with `orgId` forced on every item). Caller passes ONLY items the user
 * confirmed — drop any they deselected from the preview.
 *
 * @param {string} orgId
 * @param {object} confirmedProposal - { locations: [...], configurations: [...], contacts: [...], passwords: [...] }
 * @returns {Array<{tool: string, params: object}>}
 */
/**
 * Defensive filter: strip any field whose value is an LLM-style
 * angle-bracketed placeholder. Sonnet sometimes fills "value the PDF didn't
 * include" with strings like `<admin UPN from PDF section 1.3.7>` or
 * `<TODO>` — these would land in IT Glue as literal text. The system prompt
 * forbids this but a belt-and-braces strip here means it can't reach the
 * API even on prompt regression.
 *
 * Match rule: any string value where (after trim) the first non-whitespace
 * char is `<` and the last is `>`. Numeric IDs and normal text are untouched.
 */
const PLACEHOLDER_RE = /^\s*<[^<>]*>\s*$/;

function stripPlaceholders(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (typeof v === 'string' && PLACEHOLDER_RE.test(v)) {
      continue; // omit — would land as literal text otherwise
    }
    out[k] = v;
  }
  return out;
}

export function buildBulkCreateItems(orgId, confirmedProposal) {
  const items = [];
  for (const loc of confirmedProposal.locations || []) {
    items.push({ tool: 'create_location', params: { ...stripPlaceholders(loc), orgId } });
  }
  for (const cfg of confirmedProposal.configurations || []) {
    items.push({ tool: 'create_configuration', params: { ...stripPlaceholders(cfg), orgId } });
  }
  for (const c of confirmedProposal.contacts || []) {
    items.push({ tool: 'create_contact', params: { ...stripPlaceholders(c), orgId } });
  }
  for (const p of confirmedProposal.passwords || []) {
    // Passwords: if the `password` field itself is a placeholder, skip the
    // whole record — creating a vaulted password whose value is "<unknown>"
    // is worse than not creating it at all.
    if (typeof p.password === 'string' && PLACEHOLDER_RE.test(p.password)) continue;
    items.push({ tool: 'create_password', params: { ...stripPlaceholders(p), orgId } });
  }
  return items;
}

/**
 * Call IT-Glue-MCP bulk_create directly (no agent involved) with dry_run=false.
 * The dashboard owns this final step so the user has full control over what lands.
 *
 * @param {Array<{tool: string, params: object}>} items
 * @returns {Promise<object>} bulk_create response: {success, total, succeeded, failed, results, successfulIds, rollbackHint?}
 */
export async function confirmAndWrite(items) {
  return callMCPTool('ITGlue-MCP', 'bulk_create', {
    items,
    dry_run: false,
  });
}

// ─── 5. Walk-round template helpers (Task 11) ─────────────────────────────

/**
 * POST to /api/onboard/template. Returns {filename, pdf_base64}.
 * The backend builds a fillable AcroForm PDF from the current proposal.
 *
 * @param {string} pipelineId
 * @param {object} proposal - the current proposal object
 * @param {string} userId
 * @returns {Promise<{filename: string, pdf_base64: string}>}
 */
export async function generateTemplate(pipelineId, proposal, userId) {
  const token = localStorage.getItem('accessToken');
  const response = await fetch(`${GATEWAY_URL}/api/onboard/template`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-User-ID': userId || 'anonymous',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ pipeline_id: pipelineId, proposal }),
  });
  if (!response.ok) {
    const text = await response.text();
    let parsed;
    try { parsed = JSON.parse(text); } catch { parsed = { detail: text }; }
    const detail = parsed.detail || parsed;
    const message = typeof detail === 'string'
      ? detail
      : `Template generation failed (HTTP ${response.status})`;
    throw new Error(message);
  }
  return response.json();
}

/**
 * POST to /api/onboard/template/parse. Returns {proposal, warnings}.
 * The backend parses a filled walk-round PDF (base64) back into a corrected proposal.
 *
 * @param {string} pipelineId
 * @param {string} pdfBase64 - base64-encoded PDF bytes (no data: prefix)
 * @param {string} userId
 * @returns {Promise<{proposal: object, warnings: string[]}>}
 */
export async function parseFilledTemplate(pipelineId, pdfBase64, userId) {
  const token = localStorage.getItem('accessToken');
  const response = await fetch(`${GATEWAY_URL}/api/onboard/template/parse`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-User-ID': userId || 'anonymous',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ pipeline_id: pipelineId, pdf_base64: pdfBase64 }),
  });
  if (!response.ok) {
    const text = await response.text();
    let parsed;
    try { parsed = JSON.parse(text); } catch { parsed = { detail: text }; }
    // Preserve structured error detail (reason + detail) for the caller's
    // rejection-reason mapping (no_form_fields / pipeline_mismatch / etc.)
    const err = new Error(
      typeof parsed.detail === 'string'
        ? parsed.detail
        : `Template parse failed (HTTP ${response.status})`,
    );
    err.response = { data: parsed };
    throw err;
  }
  return response.json();
}

/**
 * Trigger a browser download from a base64-encoded PDF.
 *
 * @param {string} filename - e.g. "onboarding-walkround-12345.pdf"
 * @param {string} pdfBase64 - base64 bytes (no data: prefix)
 */
export function downloadBase64Pdf(filename, pdfBase64) {
  const bytes = Uint8Array.from(atob(pdfBase64), (ch) => ch.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Read a File (e.g. from an antd Upload beforeUpload callback) into a
 * base64 string (no data: prefix) for sending to the backend.
 *
 * @param {File} file
 * @returns {Promise<string>}
 */
export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// ─── Bonus: org lookup helper used by the page's org selector ─────────────

/**
 * List IT Glue organisations matching a name fragment (or all if none given).
 * Used to populate the target-org autocomplete on the Customer Onboarding page.
 */
export async function listItglueOrganizations(name) {
  return callMCPTool('ITGlue-MCP', 'list_organizations', name ? { name } : {});
}
